import type { ToolSelection } from './types'

// A campaign can use at most this many app actions.
export const maxActions = 20

type InputField = {
    title?: string
    default?: unknown
}

export type ToolInputSchema = {
    required?: string[]
    properties?: Record<string, InputField>
}

export type ActionChange = 'added' | 'unchanged' | 'changed'

// Catalog descriptions and schemas do not affect the selected action's configuration.
export function toolConfiguration(tools: ToolSelection[] = []) {
    return tools.map(
        ({ connectionId, slug, version, fixedArgumentsJson, policyJson, capabilityName, friendlyName }) => ({
            connectionId,
            slug,
            version,
            fixedArgumentsJson,
            ...(policyJson ? { policyJson } : {}),
            ...(capabilityName ? { capabilityName } : {}),
            ...(friendlyName ? { friendlyName } : {}),
        })
    )
}

export function inputSchema(tool: ToolSelection): ToolInputSchema {
    return JSON.parse(tool.inputSchemaJson ?? '{}')
}

export function actionKey(tool: Pick<ToolSelection, 'connectionId' | 'slug'>) {
    return `${tool.connectionId}:${tool.slug}`
}

export function actionChange(before: ToolSelection[], tool: ToolSelection): ActionChange {
    const previous = before.find((item) => actionKey(item) === actionKey(tool))

    if (!previous) {
        return 'added'
    }

    if (JSON.stringify(executionConfiguration(previous)) === JSON.stringify(executionConfiguration(tool))) {
        return 'unchanged'
    }

    return 'changed'
}

export function removedActions(before: ToolSelection[], draft: ToolSelection[]) {
    return before.filter((tool) => !draft.some((item) => actionKey(item) === actionKey(tool)))
}

function executionConfiguration(tool: ToolSelection) {
    return {
        connectionId: tool.connectionId,
        slug: tool.slug,
        version: tool.version,
        fixedArguments: canonical(tool.fixedArgumentsJson),
        policy: canonical(tool.policyJson),
    }
}

// The backend and the browser write JSON keys in different orders. Compare values, not key order.
function canonical(json: string | undefined): unknown {
    if (json === undefined) {
        return undefined
    }

    try {
        return sortKeys(JSON.parse(json))
    } catch {
        return json
    }
}

function sortKeys(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(sortKeys)
    }

    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>

        return Object.fromEntries(
            Object.keys(record)
                .sort()
                .map((key) => [key, sortKeys(record[key])])
        )
    }

    return value
}
